import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  noindex?: boolean;
  canonical?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Kingdom Studio - AI-Powered Video Creation Platform',
  description = 'Create professional videos in 30 minutes with AI. Replace 8+ tools with one platform. AI script writing, video generation, captions, thumbnails, and social media publishing. Save $150+/month.',
  keywords = 'AI video creator, video editing software, AI content creation, Christian video creator, ministry content tools, AI captions, AI thumbnails, social media video maker, content creation platform, video production software',
  image = 'https://kingdomstudio.pro/seo.png',
  url = 'https://kingdomstudio.pro',
  type = 'website',
  author = 'Kingdom Studio',
  publishedTime,
  modifiedTime,
  section,
  twitterCard = 'summary_large_image',
  noindex = false,
  canonical,
}) => {
  const siteTitle = title.includes('Kingdom Studio') ? title : `${title} | Kingdom Studio`;
  const currentUrl = url || window.location.href;
  const canonicalUrl = canonical || currentUrl;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="title" content={siteTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      {canonical && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Kingdom Studio" />
      <meta property="og:locale" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {section && <meta property="article:section" content={section} />}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:creator" content="@kingdomstudio" />
      <meta name="twitter:site" content="@kingdomstudio" />

      {/* Additional Meta Tags */}
      <meta name="application-name" content="Kingdom Studio" />
      <meta name="apple-mobile-web-app-title" content="Kingdom Studio" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      
      {/* Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Kingdom Studio',
          description: description,
          url: currentUrl,
          applicationCategory: 'MultimediaApplication',
          operatingSystem: 'Web, Windows, Mac, Linux',
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'USD',
            lowPrice: '19',
            highPrice: '199',
            offerCount: '3'
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            ratingCount: '150'
          },
          creator: {
            '@type': 'Organization',
            name: 'Kingdom Studio',
            url: 'https://kingdomstudio.pro'
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
